/* eslint-disable no-console */
import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NOTIFICATIONS, NotificationsService } from '../../core/service/notifications.service';
import { SpinnerService } from '../../core/service/spinner.service';
import { LOGIN_URLS } from '../../domain/login/module/login-routing.module';
import * as Sentry from "@sentry/angular";

export const IGNORE_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private notificationService: NotificationsService,
    private spinner: SpinnerService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return req.context?.get(IGNORE_ERROR_HANDLING) ?
      next.handle(req).pipe(catchError(this.handleError.bind(this))) :
      next.handle(req).pipe(catchError(this.handleErrorWithNotification.bind(this)));
  }

  handleError(error: any): Observable<HttpEvent<any>> {
    this.spinner.resetActiveRequests();
    let errorMessage = '';
    if (this.isChunkMessageProvided(error)) {
      this.tryToRefreshBrowser();
    }
    if (error.error instanceof ErrorEvent) {
      // client-side error
    } else {
      // server-side error
      if (error?.error === "Captcha Unsolved") {
        return throwError("Captcha Unsolved");
      }
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      switch (error.status) {
        case 401: { //  unauthorized
          break;
        }
        case 502: { // Backend just started but yet initialized
          this.router.navigate(['/maintenance']);
          break;
        }
        case 503: { // maintenance mode
          this.router.navigate(['/maintenance']);
          break;
        }
        case 451: { // 451 - Unavailable For Legal Reasons
          this.notificationService.warning(NOTIFICATIONS.UNAVAILABLE_FOR_LEGAL_REASONS);
          const URL = `/${LOGIN_URLS.base}/${LOGIN_URLS._termsAndConditions}`;
          this.router.navigate([URL]);
          break;
        }
      }
      return throwError(errorMessage);
    }
  }

  /**
   * Handles the error and tries to set the problem message in the notification service.
   * If the message containing html code with 502 from the AWS - AWS is most likely updating backend, therefore this message is ignored.
   *
   * @param error must not be null.
   */
  handleErrorWithNotification(error: any): Observable<HttpEvent<any>> {
    if (this.isAWSUpdatingBackendInstance(error?.error)) {
      return this.handleError(error);
    }

    console.log('{------------------------------');
    console.log('>>> ERROR: ', error);
    console.log('>>> ERROR STATUS: ', error.status);

    try {
      if (error.error?.error) {
        console.log('>>> ERROR MESSAGE: ', error.error.error);
        Sentry.captureException(error.error.error, {
          originalException: error,
        });
      } else if (error.error?.errors) {
        console.log('>>> ERROR MESSAGE: ', error.error.errors);
        this.notificationService.error({ title: error.error.message, message: error.error.errors });
        Sentry.captureException(error.error.errors, {
          originalException: error,
        });
      } else if (error.error?.message) {
        console.log('>>> ERROR MESSAGE: ', error.error.message);
        this.notificationService.error({ title: error.error.header, message: error.error.message });
        const sentryErrorLog = new Error(`${error.status}: '${error.error.header}': '${error.error.message}'. Place: ${error.url}`);
        Sentry.captureException(sentryErrorLog, {
          originalException: error,
        });
      } else if (error?.error === "Captcha Unsolved") {
        // notification for captcha will be executed separately
        Sentry.captureException(error.error ?? error, {
          originalException: error,
        });
        console.log('>>> ERROR MESSAGE: ', error.error ?? error);
      } else {
        this.notificationService.error({ title: 'Error', message: 'Something went wrong. Please try again later.' });
        Sentry.captureException(error.error ?? error, {
          originalException: error,
        });
        console.log('>>> ERROR MESSAGE: ', error.error ?? error);
      }
    } catch (err) {
      console.error('>>> Error while capturing the error:', err);
      Sentry.captureException(err);
    }

    console.log('------------------------------}');
    return this.handleError(error);
  }

  private isAWSUpdatingBackendInstance(error: string): boolean {
    if (!error || typeof error !== 'string') {
      return false;
    }
    return error.includes('<title>502 Bad Gateway</title>');
  }

  private isChunkMessageProvided(error: any): boolean {
    const stringedError = JSON.stringify(error);
    const isV1Satisfied = chunkFailedMessageV1.test(stringedError);
    const isV2Satisfied = stringedError.includes(chunkFailedMessageV2);
    return isV1Satisfied || isV2Satisfied;
  }

  /**
   * Checks if the browser was recently refreshed.
   * If the browser was not refreshed in this session, refreshes the browser and marks as refreshed.
   * We need it so that in case the error occurs multiple times, we do not refresh the window in a loop.
   */
  private tryToRefreshBrowser(): void {
    const isAlreadyRefreshed = sessionStorage.getItem(isAlreadyRefreshedString);
    Sentry.captureException(new Error('Chunks error, try to investigate!'));
    if (isAlreadyRefreshed) {
      Sentry.captureException(new Error('Chunks error, already refreshed :O'));
      return;
    }
    sessionStorage.setItem(isAlreadyRefreshedString, 'yes, it is indeed refreshed :c');
    window.location.reload();
  }
}

const chunkFailedMessageV1 = /Loading chunk [\d]+ failed/;
const chunkFailedMessageV2 = 'TypeError: Failed to fetch dynamically imported module';
const isAlreadyRefreshedString = 'isAlreadyRefreshed';
